/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Hub de combos: listado + alta guiada. La composición (BOM) vive en la ficha
 * del producto (pestaña Composición), no en un editor aparte.
 */
import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { colors } from '../../shared/theme/colors';
import { ticketColors } from '../../shared/theme/ticketColors';
import { spacing } from '../../shared/theme/spacing';
import { typography } from '../../shared/theme/typography';
import { matchesSearchText } from '../../shared/format/searchText';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { formatIsoDate } from '@shared/vueltoDateTime';
import { supabase } from '../../shared/supabase/supabaseClient';
import { fetchProductCombos } from '../infra/productCombosRepo';
import { comboAvailability, type ProductCombo } from '../domain/productCombos';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import {
  requestCatalogComboListView,
  requestCatalogProductDetail,
} from '../navigation/requestCatalogProductDetail';

type ProductPick = {
  id: string;
  name: string;
  sku?: string | null;
  barcode?: string | null;
  parent_product_id?: string | null;
};

function comboDayLabel(ts: string): string {
  return formatIsoDate(new Date(ts)).split('-').reverse().join('/');
}

export function ProductCombosScreen() {
  const navigation = useNavigation();
  const enabled = useHasBusinessCapability('product_combos');
  const { entitlements } = usePlanEntitlements();
  const canViewMenuOptions = useModulePermission('MenuOptions', 'view');
  const canEditMenuOptions = useModulePermission('MenuOptions', 'edit');
  const orgId = entitlements?.organization_id ?? null;
  const { width } = useWindowDimensions();
  const isWide = width >= CONTENT_WIDE_BREAKPOINT;

  const [products, setProducts] = useState<ProductPick[]>([]);
  const [combos, setCombos] = useState<Map<string, ProductCombo>>(new Map());
  const [loading, setLoading] = useState(true);
  const [pickerFilter, setPickerFilter] = useState('');

  const load = useCallback(async () => {
    if (!enabled || !orgId || !canViewMenuOptions) {
      setProducts([]);
      setCombos(new Map());
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const prodRes = await supabase
        .from('products')
        .select('id, name, sku, barcode, parent_product_id')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .or('is_misc.is.null,is_misc.eq.false')
        .is('deleted_at', null)
        .order('name')
        .limit(500);
      if (prodRes.error) throw new Error(prodRes.error.message);
      const list = (prodRes.data ?? []) as ProductPick[];
      setProducts(list);
      setCombos(await fetchProductCombos(list.map((p) => p.id)));
    } catch (e) {
      presentUnexpectedFailure({
        module: 'catalog',
        op: 'product_combos.load',
        error: e,
        userMessage: 'No se pudieron cargar los combos.',
      });
    } finally {
      setLoading(false);
    }
  }, [enabled, orgId, canViewMenuOptions]);

  const { refreshing, onRefresh } = useScreenLoadShell('ProductCombos', async () => {
    await load();
  });

  const productNameCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of products) counts.set(p.name, (counts.get(p.name) ?? 0) + 1);
    return counts;
  }, [products]);

  const productLabel = useCallback(
    (p: ProductPick): string => {
      if ((productNameCounts.get(p.name) ?? 0) <= 1) return p.name;
      const code = p.sku?.trim() || p.barcode?.trim();
      if (code) return `${p.name} · ${code}`;
      return `${p.name} · #${p.id.slice(-4)}`;
    },
    [productNameCounts],
  );

  const productName = useCallback(
    (id: string) => {
      const p = products.find((prod) => prod.id === id);
      return p ? productLabel(p) : 'Producto';
    },
    [products, productLabel],
  );

  const openProductComposition = useCallback((productId: string) => {
    requestCatalogProductDetail(productId, { tab: 'composicion', hubSegment: 'products' });
  }, []);

  const comboList = useMemo(() => {
    const q = pickerFilter.trim();
    const list = [...combos.values()];
    const filtered = q
      ? list.filter((c) => matchesSearchText(productName(c.productId), q))
      : list;
    return filtered.sort((a, b) =>
      productName(a.productId).localeCompare(productName(b.productId), 'es'),
    );
  }, [combos, pickerFilter, productName]);

  const candidateProducts = useMemo(() => {
    const q = pickerFilter.trim();
    const comboIds = new Set(combos.keys());
    return products.filter(
      (p) => !comboIds.has(p.id) && (!q || matchesSearchText(p.name, q)),
    );
  }, [products, combos, pickerFilter]);

  if (!enabled) {
    const isOwner = entitlements?.is_owner === true;
    return (
      <ShellContentFrame style={styles.shell}>
        <ModuleEmptyState
          variant="nest"
          title="Combos no disponibles"
          subtitle={
            isOwner
              ? 'Esta función está apagada. Activá Combos en Funciones del negocio.'
              : 'Este rubro no usa combos. El dueño puede activarlos desde Funciones del negocio.'
          }
          primaryAction={
            isOwner
              ? {
                  label: 'Abrir Funciones del negocio',
                  onPress: () => navigateInShell(navigation, 'BusinessFunctions'),
                  testID: 'capability.product_combos.openFunctions',
                }
              : undefined
          }
          testID="capability.product_combos.unavailable"
        />
      </ShellContentFrame>
    );
  }

  if (!canViewMenuOptions) {
    return (
      <ShellContentFrame style={styles.shell}>
        <ModuleEmptyState
          variant="nest"
          title="Combos no disponibles"
          subtitle="Pedile al dueño que te habilite Opciones de menú."
        />
      </ShellContentFrame>
    );
  }

  return (
    <ShellContentFrame style={styles.shell}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.scrollContent,
          isWide && styles.scrollContentWide,
        ]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => void onRefresh()} />
        }
      >
        <View style={styles.hero}>
          <Text style={styles.heroKicker}>Combos</Text>
          <Text style={styles.heroTitle}>Lo que vendés armado</Text>
          <Text style={styles.heroSub}>Productos con composición fija.</Text>
          <TouchableOpacity
            style={styles.linkBtn}
            onPress={() => requestCatalogComboListView()}
            testID="combos.open-product-list"
          >
            <Text style={styles.linkBtnText}>Ver combos en Productos</Text>
          </TouchableOpacity>
          {canEditMenuOptions ? (
            <TouchableOpacity
              style={styles.newComboBtn}
              onPress={() => navigateInShell(navigation, 'ComboCreateWizard')}
              testID="combos.new-wizard"
            >
              <Text style={styles.newComboBtnText}>Nuevo combo</Text>
            </TouchableOpacity>
          ) : null}
        </View>

        <TextInput
          style={styles.search}
          placeholder="Buscar producto…"
          placeholderTextColor={colors.textMuted}
          value={pickerFilter}
          onChangeText={setPickerFilter}
          testID="combos-search"
        />

        {loading ? (
          <ActivityIndicator color={colors.accent} style={styles.loader} />
        ) : (
          <>
            {comboList.length > 0 ? (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Combos armados</Text>
                {comboList.map((combo) => {
                  const availability = comboAvailability(combo);
                  return (
                    <TouchableOpacity
                      key={combo.productId}
                      style={styles.comboCard}
                      onPress={() => openProductComposition(combo.productId)}
                      testID={`combo-card-${combo.productId}`}
                    >
                      <View style={styles.comboCardBody}>
                        <Text style={styles.comboName}>
                          {productName(combo.productId)}
                        </Text>
                        <Text style={styles.comboDetail}>
                          {combo.components
                            .map((c) => `${c.qty}× ${c.name ?? c.productId}`)
                            .join(' · ')}
                        </Text>
                        {availability !== 'valid' ? (
                          <Text style={styles.comboValidityBadge}>
                            {availability === 'not_yet'
                              ? `Empieza ${combo.validFrom ? comboDayLabel(combo.validFrom) : ''}`
                              : `Venció ${combo.validUntil ? comboDayLabel(combo.validUntil) : ''}`}
                          </Text>
                        ) : combo.validFrom || combo.validUntil ? (
                          <Text style={styles.comboValidityOk}>
                            Vigente{combo.validFrom ? ` desde ${comboDayLabel(combo.validFrom)}` : ''}
                            {combo.validUntil ? ` hasta ${comboDayLabel(combo.validUntil)}` : ''}
                          </Text>
                        ) : null}
                      </View>
                      <Ionicons
                        name="chevron-forward"
                        size={18}
                        color={colors.textMuted}
                      />
                    </TouchableOpacity>
                  );
                })}
              </View>
            ) : null}

            {canEditMenuOptions ? (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>
                  {comboList.length ? 'Armar otro combo' : 'Empezá con un producto'}
                </Text>
                {candidateProducts.slice(0, 50).map((p) => (
                  <TouchableOpacity
                    key={p.id}
                    style={styles.pickRow}
                    onPress={() => openProductComposition(p.id)}
                    testID={`combo-pick-${p.id}`}
                  >
                    <Text style={styles.pickName}>{productLabel(p)}</Text>
                    <Ionicons name="add" size={18} color={colors.accent} />
                  </TouchableOpacity>
                ))}
                {candidateProducts.length === 0 ? (
                  <Text style={styles.emptyText}>
                    No hay productos para mostrar.
                  </Text>
                ) : null}
              </View>
            ) : null}
          </>
        )}
      </ScrollView>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  shell: { flex: 1 },
  scroll: { flex: 1 },
  scrollContent: {
    padding: spacing.md,
    paddingBottom: spacing.xl,
  },
  scrollContentWide: {
    maxWidth: 720,
    alignSelf: 'center',
    width: '100%',
  },
  hero: { marginBottom: spacing.md },
  heroKicker: {
    ...typography.caption,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  heroTitle: {
    ...typography.titleLg,
    color: ticketColors.ink,
    marginTop: 2,
  },
  heroSub: {
    ...typography.body,
    color: colors.textMuted,
    marginTop: 4,
  },
  linkBtn: {
    alignSelf: 'flex-start',
    marginTop: spacing.sm,
    paddingVertical: spacing.xs,
  },
  linkBtnText: {
    ...typography.body,
    color: colors.accent,
    fontWeight: '600',
  },
  newComboBtn: {
    alignSelf: 'flex-start',
    marginTop: spacing.md,
    backgroundColor: colors.accent,
    borderRadius: 10,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  newComboBtnText: {
    ...typography.body,
    fontWeight: '600',
    color: colors.surface,
  },
  search: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginBottom: spacing.md,
    color: ticketColors.ink,
    ...typography.body,
  },
  loader: { marginTop: spacing.xl },
  section: { marginBottom: spacing.lg },
  sectionTitle: {
    ...typography.caption,
    color: colors.textMuted,
    marginBottom: spacing.sm,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  comboCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    padding: spacing.md,
    marginBottom: spacing.sm,
    backgroundColor: colors.surface,
  },
  comboCardBody: { flex: 1 },
  comboName: { ...typography.body, fontWeight: '600', color: ticketColors.ink },
  comboDetail: {
    ...typography.caption,
    color: colors.textMuted,
    marginTop: 2,
  },
  comboValidityBadge: {
    ...typography.caption,
    color: ticketColors.bad,
    marginTop: 4,
    fontWeight: '600',
  },
  comboValidityOk: {
    ...typography.caption,
    color: colors.textMuted,
    marginTop: 4,
  },
  pickRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  pickName: { ...typography.body, color: ticketColors.ink, flex: 1 },
  emptyText: {
    ...typography.caption,
    color: colors.textMuted,
    paddingVertical: spacing.sm,
  },
});
